import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  VStack,
  Text,
  useToast,
  Collapse,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Container,
  Heading,
  Card,
  CardBody,
  CardHeader,
  Wrap,
  WrapItem,
  Flex,
  Icon,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { FiPieChart } from "react-icons/fi";
import {
  DashboardDataTable,
  useDashboardTableStyles,
} from "../utils/DashboardDataTable";
import SplitwiseGroups from "./SplitwiseGroup";
import SplitwiseExpenses from "./SplitwiseExpense";
import { hitBeApi } from "../../../api/api";
import TabCacheButton from "../../common/TabCacheButton";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

const SplitwiseApp = () => {
  const [groups, setGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState(null); // This handles selected group
  const [settleResponse, setSettleResponse] = useState(null); // State to store data
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showModifyExpenses, setShowModifyExpenses] = useState(false);
  const [isSendingReminders, setIsSendingReminders] = useState(false);
  const expensePanelRef = useRef(null);
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const surfaceCard = useColorModeValue("white", "gray.800");
  const mutedLine = useColorModeValue("gray.100", "gray.700");
  const boxBg = useColorModeValue("gray.50", "gray.800");
  const expensePanelBorder = useColorModeValue("gray.200", "gray.600");
  const headingColor = useColorModeValue("teal.600", "teal.300");
  const subtleText = useColorModeValue("gray.600", "gray.400");
  const settlementCardHeaderBg = useColorModeValue("gray.50", "gray.900");
  const fromColor = useColorModeValue("blue.600", "blue.300");
  const tableStyles = useDashboardTableStyles();

  // Keep selected group in sync when the groups list refreshes (members, expenses, etc.)
  // Never replace with a stale copy that has fewer members (e.g. cached GET after add-members).
  useEffect(() => {
    setCurrentGroup((prev) => {
      if (!prev?._id) return prev;
      const fresh = groups.find(
        (g) => String(g._id) === String(prev._id)
      );
      if (!fresh) return prev;
      const prevN = prev.members?.length ?? 0;
      const freshN = fresh.members?.length ?? 0;
      if (freshN < prevN) return prev;
      return fresh;
    });
  }, [groups]);

  // Fetch groups on component mount
  useEffect(() => {
    async function fetchGroups() {
      try {
        const response = await hitBeApi("splitwise/groups", "GET", null);
        if (response.success) {
          setGroups(response.data);
          if (
            response &&
            response.data &&
            response.data.length > 0 &&
            response.data[0].members.length > 0
          ) {
            dispatch({
              type: "SET_SPLITWISE_USER",
              value: response.data[0].members[0],
            });
          }
        }
      } catch (error) {
        if (error.message.includes("Unauthorized")) {
          navigate("/");
          document.cookie =
            "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
          toast({
            title: "Error",
            description: "Unauthorized: Invalid token, please login once",
            status: "error",
            duration: 2000,
            isClosable: true,
          });
          return;
        }
        toast({
          title: "Error fetching groups.",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    }
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  useEffect(() => {
    if (!showModifyExpenses) return;
    const id = window.requestAnimationFrame(() => {
      expensePanelRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [showModifyExpenses]);

  // Settle group expenses
  const settleGroup = async () => {
    if (!currentGroup) return;
    try {
      const settleResp = await hitBeApi(
        `splitwise/groups/${currentGroup._id}/settle`,
        "POST",
        null
      );

      if (settleResp.success) {
        setSettleResponse(settleResp.data);
        toast({
          title: "Group settled.",
          description: "Balances computed — see suggested transfers below.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Could not settle",
          description: settleResp.message || "Try again after adding expenses.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error settling group.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Unsettle group expenses
  const unsettleGroup = async () => {
    if (!currentGroup) return;
    try {
      await hitBeApi(
        `splitwise/groups/${currentGroup._id}/unsettle`,
        "POST",
        null
      );
      toast({
        title: "Group unsettled.",
        description: "The group expenses have been unsettled.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setSettleResponse(null); // Reset settle response when unsettled
    } catch (error) {
      toast({
        title: "Error unsettling group.",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Send payment reminder to all group members
  const sendReminderToAll = async () => {
    if (isSendingReminders) {
      return;
    }
    if (!currentGroup) return;

    try {
      setIsSendingReminders(true);
      // Send a POST request to the backend API to trigger a payment reminder for all group members
      await hitBeApi(
        `splitwise/groups/${currentGroup._id}/reminders`,
        "POST",
        null
      );
      setIsSendingReminders(false);

      // Display a success toast to confirm the reminder was sent
      toast({
        title: "Reminder sent.",
        description: "Payment reminder sent to group's payee members.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      // Display an error toast if the API call fails
      setIsSendingReminders(false);
      toast({
        title: "Error sending reminder to group's payee members.",
        description: error?.message || "An unexpected error occurred.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box minH="100vh" bg={pageBg} py={{ base: 4, md: 8 }} position="relative">
      <Container maxW="container.lg" px={{ base: 3, md: 6 }}>
        <VStack spacing={{ base: 5, md: 7 }} align="stretch">
          <Card
            overflow="hidden"
            borderRadius="2xl"
            boxShadow="lg"
            borderWidth="1px"
            borderColor={mutedLine}
            bg={surfaceCard}
          >
            <Box
              bgGradient="linear(to-r, teal.500, cyan.600)"
              px={{ base: 5, md: 8 }}
              py={{ base: 6, md: 7 }}
            >
              <Flex
                align="flex-start"
                justify="space-between"
                gap={4}
                direction={{ base: "column", sm: "row" }}
              >
                <Flex
                  align="flex-start"
                  gap={4}
                  direction={{ base: "column", sm: "row" }}
                  flex="1"
                  minW={0}
                >
                  <Flex
                    align="center"
                    justify="center"
                    w={12}
                    h={12}
                    borderRadius="xl"
                    bg="whiteAlpha.200"
                    flexShrink={0}
                  >
                    <Icon as={FiPieChart} boxSize={7} color="white" />
                  </Flex>
                  <Box>
                    <Heading
                      as="h1"
                      size="lg"
                      color="white"
                      fontWeight="extrabold"
                      letterSpacing="tight"
                    >
                      Splitwise
                    </Heading>
                    <Text
                      fontSize="sm"
                      color="whiteAlpha.900"
                      mt={2}
                      maxW="2xl"
                      lineHeight="tall"
                    >
                      Create groups, add members, log expenses, then settle balances with
                      suggested transfers.
                    </Text>
                  </Box>
                </Flex>
                <TabCacheButton tabId="splitwise" onGradient />
              </Flex>
            </Box>
          </Card>

          <SplitwiseGroups
            groups={groups}
            setGroups={setGroups}
            selectedGroup={currentGroup}
            setSelectedGroup={setCurrentGroup}
            setShowModifyExpenses={setShowModifyExpenses}
          />

          {currentGroup && showModifyExpenses && (
            <Box
              ref={expensePanelRef}
              id="splitwise-expenses-panel"
              position="relative"
              zIndex={10}
              w="100%"
              minW={0}
              maxH={{ base: "92vh", md: "88vh" }}
              overflowY="auto"
              overflowX="hidden"
              borderWidth="1px"
              borderRadius="2xl"
              borderColor={expensePanelBorder}
              bg={boxBg}
              p={{ base: 3, md: 5 }}
              boxShadow="xl"
              scrollMarginTop={{ base: "72px", md: "88px" }}
            >
              <SplitwiseExpenses state={{ currentGroup, setShowModifyExpenses }} />
            </Box>
          )}

          {currentGroup && (
            <Card
              borderRadius="xl"
              borderWidth="1px"
              borderColor={mutedLine}
              bg={surfaceCard}
              boxShadow="sm"
              overflow="hidden"
            >
              <CardHeader
                pb={2}
                borderBottomWidth="1px"
                borderColor={mutedLine}
                bg={settlementCardHeaderBg}
              >
                <Text fontSize="xs" fontWeight="bold" color={subtleText} letterSpacing="0.08em">
                  SETTLEMENT
                </Text>
                <Heading size="sm" color={headingColor} mt={1}>
                  {currentGroup.name}
                </Heading>
              </CardHeader>
              <CardBody px={{ base: 4, md: 6 }} py={5}>
                <Wrap spacing={3}>
                  <WrapItem>
                    <Button colorScheme="green" onClick={settleGroup} minW={{ base: "full", sm: "auto" }}>
                      Settle group
                    </Button>
                  </WrapItem>
                  <WrapItem>
                    <Button colorScheme="yellow" onClick={unsettleGroup} variant="outline" minW={{ base: "full", sm: "auto" }}>
                      Unsettle group
                    </Button>
                  </WrapItem>
                </Wrap>
              </CardBody>
            </Card>
          )}

          {settleResponse?.transactions &&
            settleResponse.transactions.length > 0 && (
            <Collapse in animateOpacity>
              <DashboardDataTable
                title="Suggested transfers"
                description={`${settleResponse.transactions.length} transfer${
                  settleResponse.transactions.length === 1 ? "" : "s"
                } to settle balances.`}
                maxH={{ base: "none", md: "min(50vh, 400px)" }}
              >
                <Table
                  variant="simple"
                  size="sm"
                  colorScheme="gray"
                  sx={{ borderCollapse: "separate", borderSpacing: 0 }}
                >
                  <Thead
                    position="sticky"
                    top={0}
                    zIndex={1}
                    bg={tableStyles.tableHeaderBg}
                    backdropFilter="blur(8px)"
                    borderBottomWidth="1px"
                    borderColor={tableStyles.borderColor}
                  >
                    <Tr>
                      <Th {...tableStyles.thProps}>From</Th>
                      <Th {...tableStyles.thProps}>To</Th>
                      <Th {...tableStyles.thProps} isNumeric>
                        Amount
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {settleResponse.transactions.map((transaction, index) => (
                      <Tr key={index} sx={tableStyles.trHoverSx}>
                        <Td
                          py={3}
                          fontSize="sm"
                          fontWeight="semibold"
                          color={fromColor}
                        >
                          {transaction.from.name}
                        </Td>
                        <Td py={3} fontSize="sm">
                          {transaction.to.name}
                        </Td>
                        <Td
                          isNumeric
                          py={3}
                          fontWeight="bold"
                          fontVariantNumeric="tabular-nums"
                          color={headingColor}
                        >
                          ₹{transaction.amount.toFixed(2)}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </DashboardDataTable>
            </Collapse>
          )}

          {currentGroup &&
            (!settleResponse?.transactions ||
              settleResponse.transactions.length === 0) && (
              <Alert status="info" borderRadius="xl" variant="subtle">
                <AlertIcon />
                <Text fontSize="sm">
                  Run <strong>Settle group</strong> after recording expenses to see
                  suggested transfers.
                </Text>
              </Alert>
            )}

          {currentGroup &&
            currentGroup?.members &&
            currentGroup?.members?.length > 0 && (
              <Card
                borderRadius="xl"
                borderWidth="1px"
                borderColor={mutedLine}
                bg={surfaceCard}
                boxShadow="sm"
              >
                <CardBody py={4} px={{ base: 4, md: 6 }}>
                  <Flex
                    direction={{ base: "column", sm: "row" }}
                    align={{ base: "stretch", sm: "center" }}
                    justify="space-between"
                    gap={4}
                  >
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color={headingColor}>
                        Payment reminders
                      </Text>
                      <Text fontSize="xs" color={subtleText} mt={1}>
                        Email people who owe money in this group.
                      </Text>
                    </Box>
                    <Button
                      colorScheme="blue"
                      onClick={sendReminderToAll}
                      isDisabled={isSendingReminders}
                      isLoading={isSendingReminders}
                      loadingText="Sending"
                      minW={{ base: "100%", sm: "200px" }}
                    >
                      Send reminders
                    </Button>
                  </Flex>
                </CardBody>
              </Card>
            )}
        </VStack>
      </Container>
    </Box>
  );
};

export default SplitwiseApp;
